import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  ClockIcon,
  UserGroupIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { send as sendOrQueue, isNetworkError, subscribe as subscribeOutbox } from '../lib/offline';

const QUEUED_MSG = 'No signal — saved on this phone. It posts on its own when you are back in service.';

const FieldCrewManager = () => {
  const navigate = useNavigate();
  const [crew, setCrew] = useState({ employees: [], contractors: [] });
  const [loading, setLoading] = useState(true);
  const [clockingOut, setClockingOut] = useState(false);
  const [closeOutModal, setCloseOutModal] = useState(false);
  const [closeTime, setCloseTime] = useState('');
  const [closeScope, setCloseScope] = useState('mine');
  const [closeNotes, setCloseNotes] = useState('');
  const [pendingOut, setPendingOut] = useState([]); // employee ids clocked out on this phone, waiting for signal
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const etNowTime = () =>
    new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Toronto' });

  const fetchCrew = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/shop/field-crew-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCrew(data);
      }
    } catch (err) {
      console.error('Failed to fetch crew:', err);
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => { fetchCrew(); }, [fetchCrew]);
  // Buttons read "waiting for signal" straight from the outbox; once a queued clock-out is sent, refresh the list
  useEffect(() => subscribeOutbox((snap) => {
    const ids = [];
    snap.items.filter((i) => i.type === 'crew_clock_out' && i.status === 'pending').forEach((i) => {
      if (i.payload?.all || i.payload?.mine) ids.push('*');
      ids.push(...(i.payload?.employee_ids || []), ...(i.payload?.contractor_ids || []));
    });
    setPendingOut(ids);
  }), []);
  useEffect(() => {
    const onSynced = (e) => { if (e.detail?.job?.type === 'crew_clock_out') fetchCrew(); };
    window.addEventListener('offline-synced', onSynced);
    return () => window.removeEventListener('offline-synced', onSynced);
  }, [fetchCrew]);
  const isPending = (id) => pendingOut.includes('*') || pendingOut.includes(id);

  const crewOut = async (label, body) => {
    const sent = await sendOrQueue('crew_clock_out', label, { clock_out_time: new Date().toISOString(), ...body });
    if (sent.queued) alert(QUEUED_MSG);
    return sent;
  };

  const clockOutOne = async (employeeId) => {
    setClockingOut(true);
    try {
      const who = crew.employees.find((e) => e.employee_id === employeeId)?.employee_name || 'crew member';
      await crewOut(`Clock out ${who}`, { employee_ids: [employeeId] });
      await fetchCrew();
    } catch (err) {
      alert(err.response?.data?.detail || 'Clock-out failed.');
    } finally {
      setClockingOut(false);
    }
  };

  const clockOutContractor = async (contractorId) => {
    setClockingOut(true);
    try {
      await crewOut('Clock out contractor', { contractor_ids: [contractorId] });
      await fetchCrew();
    } catch (err) {
      alert(err.response?.data?.detail || 'Clock-out failed.');
    } finally {
      setClockingOut(false);
    }
  };

  const clockOutAll = async () => {
    if (!window.confirm('Clock out all crew on site?')) return;
    setClockingOut(true);
    try {
      await crewOut('Clock out all crew on site', { all: true, notes: 'End of day — all crew' });
      await fetchCrew();
    } catch (err) {
      alert(err.response?.data?.detail || 'Clock-out failed.');
    } finally {
      setClockingOut(false);
    }
  };

  const openCloseOut = () => {
    setCloseTime(etNowTime());
    setCloseScope('mine');
    setCloseNotes('');
    setCloseOutModal(true);
  };

  const submitCloseOut = async () => {
    if (!closeTime) { alert('Pick the clock-out time.'); return; }
    setClockingOut(true);
    try {
      // HH:MM lands on each person's own day, so this is safe to upload late too
      const body = { clock_out_time: closeTime, notes: closeNotes || 'PSI close-out' };
      if (closeScope === 'mine') body.mine = true; else body.all = true;
      const sent = await sendOrQueue('crew_clock_out', `PSI close-out at ${closeTime} (${closeScope === 'mine' ? 'my crew' : 'everyone'})`, body);
      if (sent.queued) {
        alert(`📵 ${QUEUED_MSG}\n\nThe crew will be clocked out at ${closeTime}.`);
        setCloseOutModal(false);
        return;
      }
      const data = sent.data || {};
      alert(`✅ PSI CLOSED OUT\n\n${data.clocked_out || 0} crew entr${(data.clocked_out || 0) === 1 ? 'y' : 'ies'} clocked out at ${closeTime}.`);
      setCloseOutModal(false);
      await fetchCrew();
    } catch (err) {
      alert(isNetworkError(err) ? QUEUED_MSG : (err.response?.data?.detail || 'Close-out failed.'));
    } finally {
      setClockingOut(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '--';
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Toronto' });
    } catch { return '--'; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const totalOnSite = crew.employees.length + crew.contractors.length;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => navigate('/mobile/inspections')} className="mr-3">
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Field Crew</h1>
              <p className="text-blue-800 text-sm">{totalOnSite} on site</p>
            </div>
          </div>
          {totalOnSite > 0 && (
            <button
              onClick={clockOutAll}
              disabled={clockingOut}
              className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium text-sm hover:bg-red-600 disabled:opacity-50"
              data-testid="clock-out-all-btn"
            >
              Clock Out All
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {totalOnSite > 0 && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4" data-testid="psi-closeout-card">
            <p className="font-semibold text-amber-900 text-sm">End of day?</p>
            <p className="text-xs text-amber-700 mt-0.5 mb-3">Close out your PSI — everyone on it gets clocked out at the time you pick, even if you do this later from home.</p>
            <button
              onClick={openCloseOut}
              disabled={clockingOut}
              className="w-full py-3 bg-amber-600 text-white rounded-lg font-bold hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center"
              data-testid="close-out-psi-btn"
            >
              <ClockIcon className="h-5 w-5 mr-2" />
              Close Out PSI
            </button>
          </div>
        )}

        {totalOnSite === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center shadow-sm">
            <UserGroupIcon className="h-12 w-12 text-slate-700 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No crew currently on site</p>
            <p className="text-slate-500 text-sm mt-1">Crew will appear here after PSI clock-in</p>
          </div>
        ) : (
          <>
            {/* Employees */}
            {crew.employees.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-green-50 border-b border-green-200">
                  <h2 className="font-semibold text-green-800 text-sm">365 Employees ({crew.employees.length})</h2>
                </div>
                {crew.employees.map((emp) => (
                  <div key={emp.id} className="flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-0">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{emp.employee_name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <ClockIcon className="h-3.5 w-3.5" />
                        <span>In: {formatTime(emp.clock_in_time)}</span>
                        {emp.work_order_number && (
                          <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{emp.work_order_number}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => clockOutOne(emp.employee_id)}
                      disabled={clockingOut || isPending(emp.employee_id)}
                      className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 disabled:opacity-50"
                      data-testid={`clock-out-emp-${emp.employee_id}`}
                    >
                      <XMarkIcon className="h-4 w-4 inline mr-1" />
                      {isPending(emp.employee_id) ? 'Out · waiting for signal' : 'Out'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Contractors */}
            {crew.contractors.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
                  <h2 className="font-semibold text-blue-800 text-sm">Contractors ({crew.contractors.length})</h2>
                </div>
                {crew.contractors.map((con) => (
                  <div key={con.id} className="flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-0">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{con.name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <ClockIcon className="h-3.5 w-3.5" />
                        <span>In: {formatTime(con.clock_in_time)}</span>
                        {con.work_order_number && (
                          <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{con.work_order_number}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => clockOutContractor(con.id)}
                      disabled={clockingOut || isPending(con.id)}
                      className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 disabled:opacity-50"
                      data-testid={`clock-out-con-${con.id}`}
                    >
                      <XMarkIcon className="h-4 w-4 inline mr-1" />
                      {isPending(con.id) ? 'Out · waiting for signal' : 'Out'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* PSI Close-out modal */}
      {closeOutModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !clockingOut && setCloseOutModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-md p-4" onClick={(e) => e.stopPropagation()} data-testid="psi-closeout-modal">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900">Close Out PSI</h2>
              <button onClick={() => !clockingOut && setCloseOutModal(false)}><XMarkIcon className="h-5 w-5 text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clock-out time</label>
                <input
                  type="time"
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg text-gray-900 text-lg"
                  data-testid="psi-closeout-time-input"
                />
                <p className="text-xs text-gray-500 mt-1">The crew is clocked out at THIS time — pick when work actually ended.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Who to clock out</label>
                <div className="space-y-2">
                  <label className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer ${closeScope === 'mine' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                    <input type="radio" checked={closeScope === 'mine'} onChange={() => setCloseScope('mine')} className="mt-0.5" data-testid="psi-closeout-scope-mine" />
                    <span className="text-sm text-gray-800"><b>My PSI crew</b> — everyone I clocked in on my PSI</span>
                  </label>
                  <label className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer ${closeScope === 'all' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}>
                    <input type="radio" checked={closeScope === 'all'} onChange={() => setCloseScope('all')} className="mt-0.5" data-testid="psi-closeout-scope-all" />
                    <span className="text-sm text-gray-800"><b>Everyone on site</b> — all open field clock-ins</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-gray-900"
                  placeholder="e.g. Wrapped up at the shop gate"
                  data-testid="psi-closeout-notes-input"
                />
              </div>
              <button
                onClick={submitCloseOut}
                disabled={clockingOut}
                className="w-full py-3.5 bg-amber-600 text-white rounded-lg font-bold hover:bg-amber-700 disabled:opacity-50"
                data-testid="psi-closeout-confirm-btn"
              >
                {clockingOut ? 'Closing out…' : `Close Out PSI at ${closeTime || '--:--'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldCrewManager;
